/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { useUserLocation } from 'shared-ui/geolocation';
import type { SearchSuggestionGroup, SearchSuggestionItem } from '../../api/search.types';
import { useRecentLocationSearches } from './useRecentLocationSearches';
import { SUGGESTION_LOCATIONS } from './suggestionLocations';

const RANDOM_SUGGESTIONS_COUNT = 3;

/**
 * Picks n items from the array at random without replacement.
 * Uses Fisher-Yates shuffle on a copy then returns the first n elements.
 */
function pickRandom<T>(array: readonly T[], n: number): T[] {
    if (n <= 0 || array.length === 0) return [];
    const copy = [...array];
    if (copy.length <= n) return copy;
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, n);
}

export function useEmptySearchContents(): SearchSuggestionGroup[] {
    const userLocation = useUserLocation();
    const { recentSearches } = useRecentLocationSearches();

    return React.useMemo(() => {
        const nearbyGroup: SearchSuggestionGroup = {
            groupType: 'nearby',
            items: [
                {
                    text: userLocation?.location || 'Nearby',
                    icon: 'pin',
                    lat: userLocation?.latitude?.toString() ?? '0',
                    lng: userLocation?.longitude?.toString() ?? '0',
                    location: userLocation?.location,
                    id: 'nearby',
                    type: 'location',
                },
            ],
        };

        const recentGroup: SearchSuggestionGroup = {
            groupType: 'recent',
            items: recentSearches,
        };

        const recentIds = new Set(recentSearches.map((item) => item.id));
        const suggestionsExcludingRecent = SUGGESTION_LOCATIONS.filter(
            (item) => !recentIds.has(item.id)
        );
        const randomSuggestions: SearchSuggestionItem[] = pickRandom(
            suggestionsExcludingRecent,
            RANDOM_SUGGESTIONS_COUNT
        );
        const suggestionsGroup: SearchSuggestionGroup = {
            groupType: 'suggestions',
            items: randomSuggestions,
        };

        return [
            nearbyGroup,
            ...(recentSearches.length > 0 ? [recentGroup] : []),
            suggestionsGroup,
        ];
    }, [userLocation, recentSearches]);
}
